import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import './index.css';

import extra2 from './extra2.png';
import extra3 from './extra3.png';
import extra4 from './extra4.png';
import extra4Add from './extra4_add.png';
import extra5 from './extra5.png';
import extra6 from './extra6.png';
import geminiWordmark from './gemini-wordmark.svg';

import ExtensionButton from 'components/extensionButton';

const INK = '#6d4dff';

const clamp01 = n => Math.max(0, Math.min(1, n));
const smoothstep = t => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};
const revealFromArrow = t => smoothstep((t - 0.38) / 0.62);
// First arrow's pin: the board is already zoomed on that spot, then widens to the whole image.
const destZoom = (pinView, vh) => smoothstep((vh * 0.64 - pinView) / Math.max(vh * 0.2, 1));

const handCircle = (cx, cy, rx, ry) => {
  const start = -Math.PI * 0.2;
  const extra = 0.5;
  const steps = 40;
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const u = i / steps;
    const ang = start + u * Math.PI * 2 * (1 + extra);
    const past = Math.max(0, u * (1 + extra) - 1);
    const shrink = 1 - past * 0.38;
    const wob = 1 + Math.sin(ang * 2) * 0.028 + Math.sin(ang * 3 + 0.6) * 0.016;
    pts.push({
      x: cx + Math.cos(ang) * rx * wob * shrink,
      y: cy + Math.sin(ang) * ry * wob * shrink,
    });
  }
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${pts[i].x} ${pts[i].y} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L ${last.x} ${last.y}`;
};

const rule = (x1, x2, y) => `M ${x1} ${y} L ${x2} ${y}`;

const tipAt = (el, length, t) => {
  const dist = Math.max(0, Math.min(length, t * length));
  const p1 = el.getPointAtLength(dist);
  const ahead = Math.min(length, dist + 2);
  const behind = Math.max(0, dist - 2);
  const p2 = el.getPointAtLength(ahead > dist ? ahead : behind);
  const angle =
    ahead > dist
      ? Math.atan2(p2.y - p1.y, p2.x - p1.x)
      : Math.atan2(p1.y - p2.y, p1.x - p2.x);
  return { x: p1.x, y: p1.y, angle };
};

const samplesFor = (el, length, n = 96) => {
  const pts = [];
  for (let i = 0; i <= n; i += 1) pts.push(tipAt(el, length, i / n));
  return pts;
};

const tipFrom = (samples, t) => {
  if (!samples?.length) return null;
  const x = clamp01(t) * (samples.length - 1);
  const i = Math.min(Math.floor(x), samples.length - 2);
  const f = x - i;
  const a = samples[Math.max(i, 0)];
  const b = samples[Math.min(i + 1, samples.length - 1)];
  let da = b.angle - a.angle;
  if (da > Math.PI) da -= Math.PI * 2;
  if (da < -Math.PI) da += Math.PI * 2;
  return {
    x: a.x + (b.x - a.x) * f,
    y: a.y + (b.y - a.y) * f,
    angle: a.angle + da * f,
  };
};

const boundsFrom = (el, pad = 40) => {
  const len = el.getTotalLength();
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const n = 32;
  for (let i = 0; i <= n; i += 1) {
    const p = el.getPointAtLength((i / n) * len);
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { x: minX - pad, y: minY - pad, w: maxX - minX + pad * 2, h: maxY - minY + pad * 2 };
};

const unionBounds = (a, b) => {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x + a.w, b.x + b.w);
  const y2 = Math.max(a.y + a.h, b.y + b.h);
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const isKr = location.pathname === '/kr';

  const startCanvas = () => {
    if (user?.username) {
      navigate(`/${encodeURIComponent(user.username)}?create=1`);
      return;
    }
    navigate('/extension/signin');
  };

  const pageRef = useRef(null);
  const firstImgRef = useRef(null);
  const secondImgRef = useRef(null);
  const thirdImgRef = useRef(null);
  const fourthImgRef = useRef(null);
  const fifthImgRef = useRef(null);
  const measureEls = useRef([]);
  const pathEls = useRef([]);
  const tipEls = useRef([]);
  const groupEls = useRef([]);
  const circlePathRef = useRef(null);
  const circleZoomRef = useRef(null);
  const linePathRef = useRef(null);
  const addImgRef = useRef(null);
  const secondRevealRef = useRef(null);
  const thirdRevealRef = useRef(null);
  const fourthRevealRef = useRef(null);
  const fifthRevealRef = useRef(null);
  const [curve, setCurve] = useState(null);

  useEffect(() => {
    const page = pageRef.current;
    const first = firstImgRef.current;
    const second = secondImgRef.current;
    const third = thirdImgRef.current;
    if (!page || !first || !second || !third) return undefined;

    const buildPath = (pageRect, fromRect, toRect, fromAnchor, toAnchor, side = 1) => {
      const x1 = fromRect.left - pageRect.left + fromRect.width * fromAnchor.x;
      const y1 = fromRect.top - pageRect.top + fromRect.height * fromAnchor.y;
      const x2 = toRect.left - pageRect.left + toRect.width * toAnchor.x;
      const y2 = toRect.top - pageRect.top + toRect.height * toAnchor.y;

      const dy = y2 - y1;
      const mid = (x1 + x2) / 2;
      // Bow the curve sideways so it reads as a curve even when the anchors are
      // vertically aligned. Clamp it so the arc always stays on-screen.
      let bow = Math.min(Math.abs(dy) * 0.14, 180);
      bow = Math.max(0, Math.min(bow, pageRect.width - mid - 16, mid - 16));
      const d =
        `M ${x1} ${y1} ` +
        `C ${x1 + side * bow} ${y1 + dy * 0.35}, ${x2 + side * bow} ${y2 - dy * 0.35}, ${x2} ${y2}`;

      return { d, start: { x: x1, y: y1 }, end: { x: x2, y: y2 } };
    };

    const measurePath = (index, d) => {
      if (!measureEls.current[index]) {
        measureEls.current[index] = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      }
      const el = measureEls.current[index];
      el.setAttribute('d', d);
      return el;
    };

    let computing = false;
    const computeCurve = () => {
      if (computing) return;
      computing = true;
      const pageRect = page.getBoundingClientRect();
      const r1 = first.getBoundingClientRect();
      const r2 = second.getBoundingClientRect();
      const r3 = third.getBoundingClientRect();

      // Black pin card on extra4.png (3412×2020), measured from the image.
      const pin = { x: 0.706, y: 0.5767 };
      const paths = [
        buildPath(pageRect, r1, r2, { x: 0.75, y: 0.5 }, pin),
        // extra4.png is 2020px tall; start on the Google button row (y=903), 19% across.
        // Land on the Google search query in extra5 so the underline can continue from the pen.
        buildPath(pageRect, r2, r3, { x: 0.19, y: 903 / 2020 }, { x: 0.12, y: 0.145 }, -1),
      ].map((path, i) => {
        const el = measurePath(i, path.d);
        const length = el.getTotalLength();
        return { ...path, length, samples: samplesFor(el, length) };
      });

      const pinX = r2.left - pageRect.left + r2.width * pin.x;
      const pinY = r2.top - pageRect.top + r2.height * pin.y;
      const cardW = r2.width * (164 / 3412);
      const cardH = r2.height * (178 / 2020);
      const radius = Math.hypot(cardW, cardH) / 2 + 14;
      const rx = radius * 1.28;
      const ry = radius * 0.9;
      // Arrow tip is drawn 16px ahead of the path end, so stop the shaft outside
      // the oval and let the head meet the outer edge of the stroke.
      const headLead = 16;
      const shaft = measureEls.current[0];
      const approach = shaft.getPointAtLength(Math.max(paths[0].length * 0.86, 1));
      const approachAng = Math.atan2(approach.y - pinY, approach.x - pinX);
      const edge = (rx * ry) / Math.hypot(ry * Math.cos(approachAng), rx * Math.sin(approachAng));
      const touchDist = edge + 4 + headLead;
      let lo = 0;
      let hi = paths[0].length;
      for (let i = 0; i < 18; i += 1) {
        const mid = (lo + hi) / 2;
        const p = shaft.getPointAtLength(mid);
        const dist = Math.hypot(p.x - pinX, p.y - pinY);
        if (dist > touchDist) lo = mid;
        else hi = mid;
      }
      const endDist = Math.max(lo, 1);
      const steps = Math.max(16, Math.round(endDist / 10));
      const pts = [];
      for (let i = 0; i <= steps; i += 1) {
        pts.push(shaft.getPointAtLength((i / steps) * endDist));
      }
      let trimmed = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length - 1; i += 1) {
        const mx = (pts[i].x + pts[i + 1].x) / 2;
        const my = (pts[i].y + pts[i + 1].y) / 2;
        trimmed += ` Q ${pts[i].x} ${pts[i].y} ${mx} ${my}`;
      }
      const endPt = pts[pts.length - 1];
      trimmed += ` L ${endPt.x} ${endPt.y}`;
      const trimmedEl = measurePath(0, trimmed);
      const trimmedLength = trimmedEl.getTotalLength();
      paths[0] = {
        ...paths[0],
        d: trimmed,
        length: trimmedLength,
        samples: samplesFor(trimmedEl, trimmedLength),
        end: { x: endPt.x, y: endPt.y },
      };

      const circleD = handCircle(pinX, pinY, rx, ry);
      const lineY = r3.top - pageRect.top + r3.height * 0.167;
      const lineX = r3.left - pageRect.left + r3.width * 0.136;
      const lineD = rule(lineX, lineX + r3.width * 0.319, lineY);
      paths[0].markY = pinY;
      paths[1].markY = lineY;
      const circle = {
        d: circleD,
        length: measurePath(2, circleD).getTotalLength(),
        cy: pinY,
        add: {
          x: pinX,
          y: pinY,
          w: r2.width * (146 / 3418),
          h: r2.height * (150 / 1978),
        },
      };
      const line = { d: lineD, length: measurePath(3, lineD).getTotalLength(), y: lineY };
      const markerA = unionBounds(boundsFrom(measureEls.current[0]), boundsFrom(measureEls.current[2]));
      const markerB = unionBounds(boundsFrom(measureEls.current[1]), boundsFrom(measureEls.current[3]));

      const next = {
        width: pageRect.width,
        height: pageRect.height,
        imageWidth: r2.width,
        paths,
        circle,
        line,
        markerA,
        markerB,
      };
      setCurve(next);
      computing = false;
    };

    computeCurve();

    const slots = [first, second, third];
    const imageEls = slots
      .map(el => (el.tagName === 'IMG' ? el : el.querySelector('img')))
      .filter(Boolean);
    const ro = new ResizeObserver(computeCurve);
    ro.observe(page);
    slots.forEach(el => ro.observe(el));
    window.addEventListener('resize', computeCurve);

    // Layout height shifts once images finish loading, so recompute then too.
    const onLoad = () => computeCurve();
    imageEls.forEach(img => {
      if (!img.complete) img.addEventListener('load', onLoad);
    });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computeCurve);
      imageEls.forEach(img => img.removeEventListener('load', onLoad));
    };
  }, [isKr]);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const data = curve;
    if (!page || !data) return undefined;

    const narrowMql = window.matchMedia('(max-width: 768px)');
    const reduceMql = window.matchMedia('(prefers-reduced-motion: reduce)');
    let pageDocTop = 0;
    let fourthDoc = 0;
    let fifthDoc = 0;
    let narrow = narrowMql.matches;

    const cacheLayout = () => {
      const scrollY = window.scrollY;
      pageDocTop = page.getBoundingClientRect().top + scrollY;
      fourthDoc = fourthImgRef.current
        ? fourthImgRef.current.getBoundingClientRect().top + scrollY
        : 0;
      fifthDoc = fifthImgRef.current
        ? fifthImgRef.current.getBoundingClientRect().top + scrollY
        : 0;
      narrow = narrowMql.matches;
    };

    const read = () => {
      const vh = window.innerHeight;
      const viewportTop = pageDocTop - window.scrollY;
      const revealY = vh * 0.58;
      const reduce = reduceMql.matches;
      const arrows = data.paths.map((path, i) => {
        const length = path.length || 0;
        if (!length) return { t: 0, length: 0, tip: path.start, mark: 0, pinView: vh, samples: path.samples };
        const markY = viewportTop + (path.markY ?? path.end.y);
        if (reduce) {
          return { t: 1, length, tip: tipFrom(path.samples, 1), mark: 1, pinView: 0, samples: path.samples };
        }
        const startY = viewportTop + path.start.y;
        const endY = viewportTop + path.end.y;
        const span = Math.max(endY - startY, 1);
        const t = clamp01((revealY - startY) / span);
        // Draw the destination circle while the image is still zoomed in on the pin.
        const markFrom = i === 0 ? 0.98 : 0.82;
        const markSpan = i === 0 ? 0.22 : 0.32;
        const mark = smoothstep((vh * markFrom - markY) / Math.max(vh * markSpan, 1));
        return { t, length, mark, pinView: markY, samples: path.samples, tip: path.start };
      });
      const revealFor = docTop => {
        if (reduce) return 1;
        const top = docTop - window.scrollY;
        return smoothstep((vh * 0.9 - top) / Math.max(vh * 0.48, 1));
      };
      return { arrows, fourth: revealFor(fourthDoc), fifth: revealFor(fifthDoc) };
    };

    const apply = ({ arrows, fourth, fifth }) => {
      arrows.forEach((item, i) => {
        const path = pathEls.current[i];
        const group = groupEls.current[i];
        const tip = tipEls.current[i];
        if (!path || !group) return;
        const show = item.t > 0.001;
        if (group.style.visibility !== (show ? 'visible' : 'hidden')) {
          group.style.visibility = show ? 'visible' : 'hidden';
        }
        if (!show) return;
        const drawn = tipFrom(item.samples, item.t) || item.tip;
        path.setAttribute('stroke-dashoffset', String(item.length * (1 - item.t)));
        if (tip && drawn) {
          tip.setAttribute(
            'transform',
            `translate(${drawn.x} ${drawn.y}) rotate(${(drawn.angle * 180) / Math.PI})`,
          );
        }
      });

      const vh = window.innerHeight;
      const pinView = arrows[0]?.pinView ?? vh;
      const zoom = destZoom(pinView, vh);
      const mark0 = arrows[0]?.mark ?? 0;
      const mark1 = arrows[1]?.mark ?? 0;
      if (circlePathRef.current && data.circle) {
        const show = mark0 > 0.01;
        const group = circlePathRef.current.parentNode;
        if (group?.style) group.style.visibility = show ? 'visible' : 'hidden';
        if (show) {
          circlePathRef.current.setAttribute(
            'stroke-dashoffset',
            String(data.circle.length * (1 - mark0)),
          );
        }
      }
      if (linePathRef.current && data.line) {
        const show = mark1 > 0.01;
        const group = linePathRef.current.parentNode;
        if (group?.style) group.style.visibility = show ? 'visible' : 'hidden';
        if (show) {
          linePathRef.current.setAttribute(
            'stroke-dashoffset',
            String(data.line.length * (1 - mark1)),
          );
        }
      }
      if (circleZoomRef.current) {
        circleZoomRef.current.style.setProperty('--zoom', String(zoom));
      }
      if (addImgRef.current) {
        const add = smoothstep((mark0 - 0.58) / 0.42);
        addImgRef.current.style.setProperty('--add', String(add));
        addImgRef.current.style.setProperty('--zoom', String(zoom));
      }
      if (secondRevealRef.current) {
        secondRevealRef.current.style.setProperty('--zoom', String(zoom));
      }
      if (thirdRevealRef.current) {
        thirdRevealRef.current.style.setProperty('--reveal', String(revealFromArrow(arrows[1]?.t ?? 0)));
      }
      if (fourthRevealRef.current) fourthRevealRef.current.style.setProperty('--reveal', String(fourth));
      if (fifthRevealRef.current) fifthRevealRef.current.style.setProperty('--reveal', String(fifth));
    };

    const targetsRef = { current: [] };
    const animatedRef = { current: [] };
    const extraRef = { current: { fourth: 0, fifth: 0 } };
    let drawRaf = 0;

    const settle = () => {
      const targets = targetsRef.current;
      const prev = animatedRef.current;
      let moving = false;
      const next = targets.map((target, i) => {
        const fromT = prev[i]?.t ?? 0;
        let t = fromT + (target.t - fromT) * 0.18;
        if (Math.abs(target.t - t) < 0.004) t = target.t;
        else moving = true;
        return { ...target, t };
      });
      animatedRef.current = next;
      apply({ arrows: next, fourth: extraRef.current.fourth, fifth: extraRef.current.fifth });
      drawRaf = moving ? requestAnimationFrame(settle) : 0;
    };

    const tick = () => {
      const next = read();
      extraRef.current = { fourth: next.fourth, fifth: next.fifth };
      if (narrow || reduceMql.matches) {
        if (drawRaf) {
          cancelAnimationFrame(drawRaf);
          drawRaf = 0;
        }
        animatedRef.current = next.arrows;
        apply(next);
        return;
      }
      targetsRef.current = next.arrows;
      if (!drawRaf) drawRaf = requestAnimationFrame(settle);
    };

    let frame = 0;
    const onScroll = () => {
      if (narrow || reduceMql.matches) {
        tick();
        return;
      }
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        tick();
      });
    };

    const onMode = () => {
      cacheLayout();
      tick();
    };

    cacheLayout();
    tick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onMode);
    narrowMql.addEventListener('change', onMode);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onMode);
      narrowMql.removeEventListener('change', onMode);
      if (frame) cancelAnimationFrame(frame);
      if (drawRaf) cancelAnimationFrame(drawRaf);
    };
  }, [curve]);

  const openTwitter = () => {
    window.open('https://reddit.com/r/hashely', '_blank');
  };

  const copy = isKr
    ? {
        beta: '베타',
        headline: 'AI에게 무엇이든 기억시키세요.',
        subheadline: (
          <>
            <strong>Google, Reddit, AI 채팅</strong>
            —어디서든 무엇이든 모으세요. AI가 필요한 것을 자동으로 찾아냅니다.
          </>
        ),
        feature1: '웹 어디서든 무엇이든 핀하기',
        feature2: 'AI가 다음에 물을 것을 제안합니다',
        canvasCta: 'AI 화이트보드 무료로 시작하기',
        rightClickHint: '오른쪽 클릭',
        ex1: {
          title: '기억시킬 영역을 선택하세요.',
          desc: [
            '페이지 전체를 저장할 필요는 없습니다. 중요한 부분만 선택하세요.',
            '오른쪽 클릭으로 그 선택만 캔버스에 고정하세요.',
          ],
        },
        ex2: {
          badge: '나만의 웹 지도',
          title: 'AI가 이미 저장된 기억들과 결합해 당신을 대신해 생각합니다.',
          desc: [
            '핀, 텍스트, 메모 등. AI 메모리 보드에 무엇이든 저장하세요.',
            <></>,
          ],
        },
        exNext: {
          badge: '최적화된 검색',
          title: 'AI가 지금 가장 필요한 다음을 찾아냅니다.',
          desc: [
            '다음에 무엇을 물을지 고민할 필요 없습니다. 버튼만 누르면 다음 검색이 시작됩니다.',
            '보드 전체를 분석해 AI 프롬프트 엔지니어가 다음 프롬프트를 만듭니다.',
          ],
        },
        exAsk: {
          title: 'AI 답변을 다시 메모리에 핀하세요.',
          desc: [
            'GPT, Gemini, Claude 등. 이미 쓰던 AI에 보드를 컨텍스트로 더해 사용하세요.',
            'AI 답변을 보드에 핀하고 여정을 이어가세요.',
          ],
        },
        ex3: {
          badge: '보안',
          title: '언제나 안전합니다',
          desc: [
            'Hashely는 웹페이지의 공개 URL에 라벨만 저장합니다',
            '공개 페이지에서만 작동하며 개인정보에는 접근하지 않습니다',
          ],
        },
      }
    : {
        beta: 'Beta',
        headline: (
          <>
           Let AI remember anything.
          </>
        ),
   
        subheadline: (
          <>
            <strong>Google, Reddit, AI chats</strong>
            —collect anything from anywhere. AI gives you the next insight for your goal.
          </>
        ),
        feature1: 'Pin anything on the web',
        feature2: 'AI suggests what to ask next',
        canvasCta: 'Start AI whiteboard for free',
        rightClickHint: 'Right-click',
        ex1: {
          title: 'Select anything related to your goal.',
          desc: [
            'You don\'t have to save the whole page. Select only the part that matters.',
            'Right-click to pin just that selection to your AI memory board.',
          ],
        },
        ex2: {
          badge: 'Your personal web map',
          title: 'AI connects it as a combined memory to discover insights.',
          desc: [
            'Pins, text, notes, and more. Save anything on the AI memory board.',
            <>
            </>,
          ],
        },
        exNext: {
          badge: 'Optimized search',
          title: 'AI gives you the next insight, instantly.',
          desc: [
            'You don\'t have to ponder what to ask next. Just click the button to search the next.',
            'By analyzing your entire board, AI prompt engineer builds the next prompt.',
          ],
        },
        exAsk: {
          title: 'Ask AI anything with your combined memories.',
          desc: [
            'GPT, Gemini, Claude, etc. Use the AI you are already using, adding your board as a context.',
            'Pin the AI answer to the board and continue the journey.',
          ],
        },
        ex3: {
          badge: 'Security',
          title: 'Always safe',
          desc: [
            'Hashely saves your labels on the public URL of a webpage',
            'It only works on public pages and cannot access your private information',
          ],
        },
      };

  return (
    <div className="home-page" ref={pageRef}>
      {curve && (
        <svg
          className="home-curve"
          width={curve.width}
          height={curve.height}
          viewBox={`0 0 ${curve.width} ${curve.height}`}
          fill="none"
          aria-hidden="true"
        >
          <defs>
            {[
              ['homeMarkerA', curve.markerA],
              ['homeMarkerB', curve.markerB],
            ].map(([id, box]) => (
              <filter
                key={id}
                id={id}
                filterUnits="userSpaceOnUse"
                x={box.x}
                y={box.y}
                width={box.w}
                height={box.h}
                colorInterpolationFilters="sRGB"
              >
                <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed="3" result="noise" />
                <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.7" xChannelSelector="R" yChannelSelector="G" />
              </filter>
            ))}
          </defs>
          {curve.paths.map((path, i) => (
            <g
              key={i}
              ref={node => { groupEls.current[i] = node; }}
              filter={`url(#homeMarker${i === 0 ? 'A' : 'B'})`}
            >
              <path
                ref={node => { pathEls.current[i] = node; }}
                className="home-curve-path"
                d={path.d}
                stroke={INK}
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={path.length}
              />
              <g ref={node => { tipEls.current[i] = node; }} className="home-curve-tip">
                <path
                  d="M 16 0 L -2 -13"
                  fill="none"
                  stroke={INK}
                  strokeWidth="8"
                  strokeLinecap="round"
                />
                <path
                  d="M 16 0 L -1 14"
                  fill="none"
                  stroke={INK}
                  strokeWidth="8"
                  strokeLinecap="round"
                />
              </g>
            </g>
          ))}
          {curve.circle && (
            <g
              ref={circleZoomRef}
              className="home-dest-zoom"
              style={{
                transformBox: 'view-box',
                transformOrigin: `${curve.circle.add.x}px ${curve.circle.add.y}px`,
              }}
            >
              <g className="home-ink-mark" filter="url(#homeMarkerA)">
                <path
                  ref={circlePathRef}
                  d={curve.circle.d}
                  fill="none"
                  stroke={INK}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={curve.circle.length}
                />
              </g>
            </g>
          )}
          {curve.line && (
            <g className="home-ink-mark" filter="url(#homeMarkerB)">
              <path
                ref={linePathRef}
                d={curve.line.d}
                fill="none"
                stroke={INK}
                strokeWidth="6.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={curve.line.length}
              />
            </g>
          )}
        </svg>
      )}
      {curve?.circle?.add && (
        <img
          ref={addImgRef}
          className="home-circle-add"
          src={extra4Add}
          alt=""
          style={{
            left: curve.circle.add.x,
            top: curve.circle.add.y,
            width: curve.circle.add.w,
            height: curve.circle.add.h,
          }}
        />
      )}
      <section className="home-hero">
        <div className="home-hero-top">
        <div className="home-hero-content">
          <span className="home-beta-mark">{copy.beta}</span>
          <h1 className="home-headline">{copy.headline}</h1>
          <p className="home-subheadline">{copy.subheadline}</p>

          {/* <div className="home-features-row">
            <span className="home-feature-pill">
              <ChatBubbleOutlineIcon />
              {copy.feature1}
            </span>
            <span className="home-feature-pill">
              <BookmarkBorderIcon />
              {copy.feature2}
            </span>
          </div> */}

          <div className="home-cta-row">
            <Button className="home-canvas-cta" onClick={startCanvas}>
              {copy.canvasCta}
            </Button>
            <ExtensionButton />
            <div className="home-links-row">
              <Button className="home-link-btn" onClick={openTwitter}>
                Contact
              </Button>
              <Button className="home-link-btn" onClick={() => navigate('/privacy')}>
                Privacy
              </Button>
            </div>
          </div>

          <div className="home-powered-by">
            <span>Powered by</span>
            <img
              src={geminiWordmark}
              alt="Gemini wordmark"
              style={{ width: 100, height: 'auto', objectFit: 'contain' }}
            />
          </div>
        </div>

        <div className="home-hero-demo" aria-hidden="true">
          <svg className="home-hero-demo-svg" viewBox="0 0 280 378">
            <defs>
              <clipPath id="homeHeroDemoWindow">
                <rect x="0" y="0" width="280" height="180" rx="14" />
              </clipPath>
              <clipPath id="homeHeroDemoPage">
                <rect x="0" y="30" width="280" height="150" />
              </clipPath>
              <clipPath id="homeHeroDemoThumb">
                <rect x="16" y="224" width="44" height="36" rx="5" />
              </clipPath>
              <clipPath id="homeHeroDemoFlyThumb">
                <rect x="2" y="46" width="44" height="36" rx="5" />
              </clipPath>
              <clipPath id="homeHeroDemoHero">
                <rect x="16" y="90" width="248" height="56" rx="8" />
              </clipPath>
              <clipPath id="homeHeroDemoCanvas">
                <rect x="0" y="198" width="280" height="180" rx="14" />
              </clipPath>
              <filter id="homeHeroDemoShadow" x="-12%" y="-12%" width="124%" height="140%">
                <feDropShadow
                  dx="0"
                  dy="8"
                  stdDeviation="7"
                  floodColor="#0f172a"
                  floodOpacity="0.14"
                />
              </filter>
            </defs>
            <g filter="url(#homeHeroDemoShadow)">
            <g clipPath="url(#homeHeroDemoWindow)">
              <rect width="280" height="180" fill="#ffffff" />
              <g clipPath="url(#homeHeroDemoPage)">
                <g className="home-hero-demo-page">
                  <circle cx="22" cy="46" r="6" fill="#e2e8f0" />
                  <rect x="34" y="43" width="30" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="68" y="43" width="22" height="6" rx="3" fill="#f1f5f9" />
                  <rect x="94" y="43" width="22" height="6" rx="3" fill="#f1f5f9" />
                  <circle cx="258" cy="46" r="6" fill="#f1f5f9" />

                  <rect x="16" y="62" width="168" height="10" rx="3" fill="#cbd5e1" />
                  <rect x="16" y="78" width="104" height="6" rx="2" fill="#e2e8f0" />

                  <rect x="16" y="90" width="248" height="56" rx="8" fill="#eef2ff" />
                  <g clipPath="url(#homeHeroDemoHero)">
                    <circle cx="48" cy="126" r="22" fill="#c7d2fe" />
                    <path d="M16 146 L54 112 L72 128 L96 108 L140 146 Z" fill="#c7d2fe" />
                    <rect x="150" y="102" width="96" height="8" rx="2" fill="#a5b4fc" />
                    <rect x="150" y="116" width="72" height="5" rx="2" fill="#c7d2fe" />
                    <rect x="150" y="126" width="88" height="5" rx="2" fill="#c7d2fe" />
                  </g>

                  <rect x="16" y="156" width="76" height="40" rx="6" fill="#f1f5f9" />
                  <rect x="24" y="164" width="44" height="6" rx="2" fill="#e2e8f0" />
                  <rect x="24" y="176" width="32" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="100" y="156" width="76" height="40" rx="6" fill="#f1f5f9" />
                  <rect x="108" y="164" width="44" height="6" rx="2" fill="#e2e8f0" />
                  <rect x="108" y="176" width="36" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="184" y="156" width="80" height="40" rx="6" fill="#f1f5f9" />
                  <rect x="192" y="164" width="48" height="6" rx="2" fill="#e2e8f0" />
                  <rect x="192" y="176" width="28" height="5" rx="2" fill="#e2e8f0" />

                  <rect x="16" y="208" width="52" height="6" rx="2" fill="#cbd5e1" />
                  <rect x="16" y="224" width="44" height="36" rx="5" fill="#e2e8f0" />
                  <g clipPath="url(#homeHeroDemoThumb)">
                    <circle cx="30" cy="236" r="4" fill="#cbd5e1" />
                    <path d="M16 260 L32 240 L39 248 L48 236 L60 260 Z" fill="#cbd5e1" />
                  </g>
                  <rect x="70" y="228" width="112" height="8" rx="2" fill="#64748b" />
                  <rect x="70" y="242" width="156" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="70" y="252" width="132" height="5" rx="2" fill="#e2e8f0" />

                  <rect x="16" y="270" width="200" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="16" y="282" width="148" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="16" y="296" width="176" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="16" y="308" width="120" height="5" rx="2" fill="#e2e8f0" />

                  <rect
                    className="home-hero-demo-select"
                    x="14"
                    y="222"
                    width="216"
                    height="40"
                    rx="3"
                    fill="none"
                    stroke="#03c04a"
                    strokeWidth="1.6"
                    strokeDasharray="4 3"
                    strokeLinejoin="round"
                  />
                </g>
              </g>

              <rect width="280" height="30" fill="#f8fafc" />
              <line x1="0" y1="30" x2="280" y2="30" stroke="#e2e8f0" strokeWidth="1" />
              <circle cx="16" cy="15" r="3.5" fill="#ff5f57" />
              <circle cx="30" cy="15" r="3.5" fill="#febc2e" />
              <circle cx="44" cy="15" r="3.5" fill="#28c840" />
              <rect x="64" y="9" width="152" height="13" rx="6.5" fill="#ffffff" stroke="#e8edf3" />
              <rect x="76" y="13.5" width="72" height="4" rx="2" fill="#e2e8f0" />

              <rect x="271" y="36" width="5" height="136" rx="2.5" fill="#e2e8f0" />
              <rect
                className="home-hero-demo-scrollthumb"
                x="271"
                y="36"
                width="5"
                height="48"
                rx="2.5"
                fill="#64748b"
              />
            </g>
            <rect
              x="0.5"
              y="0.5"
              width="279"
              height="179"
              rx="13.5"
              fill="none"
              stroke="rgba(15, 23, 42, 0.08)"
            />
            </g>

            <g className="home-hero-demo-canvas-window" filter="url(#homeHeroDemoShadow)">
            <g clipPath="url(#homeHeroDemoCanvas)">
              <rect x="0" y="198" width="280" height="180" fill="#ffffff" />

              <g transform="translate(56 246)">
                <rect width="58" height="70" rx="6" fill="#ffffff" stroke="#eeeeee" strokeWidth="0.8" />
                <rect width="58" height="14" rx="6" fill="#ffffff" />
                <rect y="8" width="58" height="6" fill="#ffffff" />
                <circle cx="9" cy="7" r="3" fill="#64748b" />
                <circle cx="8.2" cy="6.2" r="1.1" fill="#94a3b8" />
                <text className="home-hero-demo-pin-title" x="15" y="9">Hokkaido</text>
                <path d="M48 4.5 L53 9.5 M53 4.5 L48 9.5" stroke="#cbd5e1" strokeWidth="0.9" strokeLinecap="round" />
                <line x1="0" y1="14" x2="58" y2="14" stroke="#f0f0f0" strokeWidth="0.6" />
                <rect x="3" y="17" width="52" height="50" rx="3" fill="#f8fafc" />
                <rect x="5" y="19" width="48" height="7" rx="1.5" fill="#e2e8f0" />
                <rect x="5" y="28" width="18" height="22" rx="2" fill="#cbd5e1" />
                <circle cx="10" cy="34" r="2.2" fill="#94a3b8" />
                <path d="M5 50 L12 40 L16 45 L21 36 L23 50 Z" fill="#94a3b8" />
                <rect x="26" y="29" width="24" height="3" rx="1" fill="#1e1b4b" />
                <rect x="26" y="34" width="20" height="2" rx="0.8" fill="#cbd5e1" />
                <rect x="26" y="38" width="22" height="2" rx="0.8" fill="#e2e8f0" />
                <rect x="5" y="53" width="40" height="2" rx="0.8" fill="#e2e8f0" />
                <rect x="5" y="57" width="28" height="2" rx="0.8" fill="#e2e8f0" />
                <rect x="5" y="61" width="34" height="2" rx="0.8" fill="#e2e8f0" />
              </g>

              <g transform="translate(136 246)">
                <rect width="52" height="52" rx="6" fill="#ffffff" stroke="#eeeeee" strokeWidth="0.8" />
                <rect width="52" height="13" rx="6" fill="#ffffff" />
                <rect y="7" width="52" height="6" fill="#ffffff" />
                <circle cx="8" cy="6.5" r="2.6" fill="#7dd3fc" />
                <text className="home-hero-demo-pin-title" x="13.5" y="8.5">Sapporo</text>
                <path d="M43 3.8 L47.5 8.2 M47.5 3.8 L43 8.2" stroke="#cbd5e1" strokeWidth="0.85" strokeLinecap="round" />
                <line x1="0" y1="13" x2="52" y2="13" stroke="#f0f0f0" strokeWidth="0.6" />
                <rect x="3" y="16" width="46" height="33" rx="3" fill="#dbeafe" />
                <circle cx="14" cy="42" r="10" fill="#93c5fd" />
                <path d="M3 49 L18 30 L26 38 L34 26 L49 49 Z" fill="#64748b" />
                <circle cx="40" cy="24" r="3.2" fill="#fde68a" />
              </g>

              <g transform="translate(56 328)">
                <rect width="64" height="38" rx="6" fill="#ffffff" stroke="#eeeeee" strokeWidth="0.8" />
                <rect width="64" height="13" rx="6" fill="#ffffff" />
                <rect y="7" width="64" height="6" fill="#ffffff" />
                <circle cx="9" cy="6.5" r="3.1" fill="#ff0000" />
                <path d="M8.1 5.2 L11.2 6.5 L8.1 7.8 Z" fill="#ffffff" />
                <text className="home-hero-demo-pin-title" x="15" y="8.5">A Snowy Walk</text>
                <path d="M54 3.8 L58.5 8.2 M58.5 3.8 L54 8.2" stroke="#cbd5e1" strokeWidth="0.85" strokeLinecap="round" />
                <line x1="0" y1="13" x2="64" y2="13" stroke="#f0f0f0" strokeWidth="0.6" />
                <rect x="3" y="16" width="58" height="19" rx="2.5" fill="#0f172a" />
                <rect x="3" y="16" width="58" height="19" rx="2.5" fill="#334155" />
                <circle cx="32" cy="25.5" r="5.2" fill="#ff0000" />
                <path d="M30.6 23 L35.2 25.5 L30.6 28 Z" fill="#ffffff" />
              </g>

              <rect x="0" y="198" width="280" height="30" fill="#f8fafc" />
              <line x1="0" y1="228" x2="280" y2="228" stroke="#e2e8f0" strokeWidth="1" />
              <circle cx="16" cy="213" r="3.5" fill="#ff5f57" />
              <circle cx="30" cy="213" r="3.5" fill="#febc2e" />
              <circle cx="44" cy="213" r="3.5" fill="#28c840" />
              <rect x="58" y="207" width="198" height="13" rx="6.5" fill="#ffffff" stroke="#e8edf3" />
              <text className="home-hero-demo-canvas-url" x="68" y="216">
                https://hashely.me/:your_name
              </text>
            </g>
            <rect
              x="0.5"
              y="198.5"
              width="279"
              height="179"
              rx="13.5"
              fill="none"
              stroke="rgba(15, 23, 42, 0.08)"
            />
            </g>

            <g className="home-hero-demo-fly">
              <rect
                x="0"
                y="28"
                width="216"
                height="74"
                rx="3"
                fill="#ffffff"
                stroke="#eeeeee"
                strokeWidth="1.2"
              />
              <rect x="0" y="28" width="216" height="8" fill="#ffffff" />
              <g className="home-hero-demo-fly-header">
                <rect
                  width="216"
                  height="28"
                  rx="8"
                  fill="#ffffff"
                  stroke="#eeeeee"
                  strokeWidth="1.2"
                />
                <rect y="20" width="216" height="10" fill="#ffffff" />
                <circle cx="16" cy="14" r="6" fill="#64748b" />
                <circle cx="14.4" cy="12.4" r="2.2" fill="#94a3b8" />
                <text className="home-hero-demo-fly-title" x="28" y="18">
                  Winter travel
                </text>
                <path
                  d="M198 9 L208 19 M208 9 L198 19"
                  stroke="#cbd5e1"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <line x1="0" y1="28" x2="216" y2="28" stroke="#f0f0f0" strokeWidth="1" />
              </g>
              <rect x="2" y="30" width="52" height="6" rx="2" fill="#cbd5e1" />
              <rect x="2" y="46" width="44" height="36" rx="5" fill="#e2e8f0" />
              <g clipPath="url(#homeHeroDemoFlyThumb)">
                <circle cx="16" cy="58" r="4" fill="#cbd5e1" />
                <path d="M2 82 L18 62 L25 70 L34 58 L46 82 Z" fill="#cbd5e1" />
              </g>
              <rect x="56" y="50" width="112" height="8" rx="2" fill="#64748b" />
              <rect x="56" y="64" width="156" height="5" rx="2" fill="#e2e8f0" />
              <rect x="56" y="74" width="132" height="5" rx="2" fill="#e2e8f0" />
              <rect x="2" y="92" width="200" height="5" rx="2" fill="#e2e8f0" />
            </g>

            <g className="home-hero-demo-hint">
              <rect
                x="102"
                y="86"
                width="92"
                height="22"
                rx="7"
                fill="#0f172a"
              />
              <path d="M 94 108 L 102 100 L 102 110 Z" fill="#0f172a" />
              <rect
                x="109"
                y="90.5"
                width="9"
                height="13"
                rx="4.5"
                fill="none"
                stroke="#cbd5e1"
                strokeWidth="0.9"
              />
              <line x1="113.5" y1="90.5" x2="113.5" y2="96.5" stroke="#cbd5e1" strokeWidth="0.7" />
              <path d="M113.5 90.5 H117.5 A4.5 4.5 0 0 1 118 95 V96.5 H113.5 Z" fill="#03c04a" />
              <text className="home-hero-demo-hint-label" x="123" y="101">
                {copy.rightClickHint}
              </text>
            </g>
            <g className="home-hero-demo-menu">
              <rect
                x="88"
                y="108"
                width="160"
                height="70"
                rx="8"
                fill="#ffffff"
                stroke="#e2e8f0"
              />
              <rect x="100" y="118" width="72" height="5" rx="2" fill="#cbd5e1" />
              <rect x="100" y="132" width="88" height="5" rx="2" fill="#cbd5e1" />
              <line x1="96" y1="144" x2="240" y2="144" stroke="#e2e8f0" />
              <rect
                className="home-hero-demo-menu-pin"
                x="92"
                y="148"
                width="152"
                height="26"
                rx="5"
              />
              <rect
                x="100"
                y="155"
                width="11"
                height="9"
                rx="1.5"
                fill="none"
                stroke="#03c04a"
                strokeWidth="1.15"
                strokeDasharray="1.6 1.1"
              />
              <text className="home-hero-demo-menu-label" x="116" y="163.5">
                Pin it to your Ai canvas
              </text>
            </g>
            <g className="home-hero-demo-cursor">
              <path
                d="M0 0 L0 14.2 3.9 10.4 6.7 17.2 9.3 16.1 6.4 9.2 11 9.2 Z"
                fill="#03c04a"
                stroke="#ffffff"
                strokeWidth="0.7"
                strokeLinejoin="round"
              />
            </g>
          </svg>
        </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">{copy.ex1.title}</h2>
          <p className="home-section-desc">{copy.ex1.desc[0]}</p>
          <p className="home-section-desc">{copy.ex1.desc[1]}</p>
        </div>
        <div ref={firstImgRef} className="home-section-image-slot">
          <img
            className="home-section-image"
            src={extra2}
            alt="Selecting the essence of a webpage with Hashely"
          />
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <span className="home-section-badge home-section-badge--blue">
            {copy.ex2.badge}
          </span>
          <h2 className="home-section-title">{copy.ex2.title}</h2>
          <p className="home-section-desc">{copy.ex2.desc[0]}</p>
          <p className="home-section-desc">{copy.ex2.desc[1]}</p>
        </div>
        <div ref={secondImgRef} className="home-section-image-slot">
          <div className="home-section-image-frame">
            <img
              ref={secondRevealRef}
              className="home-section-image home-section-image--focus"
              src={extra4}
              alt="Board canvas with saved bookmarks arranged on your page"
            />
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">{copy.exNext.title}</h2>
          <p className="home-section-desc">{copy.exNext.desc[0]}</p>
          <p className="home-section-desc">{copy.exNext.desc[1]}</p>
        </div>
        <div ref={thirdImgRef} className="home-section-image-slot">
          <img
            ref={thirdRevealRef}
            className="home-section-image home-section-image--reveal home-section-image--from-left"
            src={extra5}
            alt="Click to run the next search from your board"
          />
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">{copy.exAsk.title}</h2>
          <p className="home-section-desc">{copy.exAsk.desc[0]}</p>
          <p className="home-section-desc">{copy.exAsk.desc[1]}</p>
        </div>
        <div ref={fifthImgRef} className="home-section-image-slot">
          <img
            ref={fifthRevealRef}
            className="home-section-image home-section-image--reveal"
            src={extra6}
            alt="Asking Gemini a question with the whiteboard as context"
          />
        </div>
      </section>

      <section className="home-section home-section--muted">
        <div className="home-section-inner">
          <span className="home-section-badge home-section-badge--slate">
            {copy.ex3.badge}
          </span>
          <h2 className="home-section-title">{copy.ex3.title}</h2>
          <p className="home-section-desc">{copy.ex3.desc[0]}</p>
          <p className="home-section-desc">{copy.ex3.desc[1]}</p>
        </div>
        <div ref={fourthImgRef} className="home-section-image-slot home-section-image-slot--muted">
          <img
            ref={fourthRevealRef}
            className="home-section-image home-section-image--reveal"
            src={extra3}
            alt="The Nexus of AI and Security — Google Cloud Security"
          />
        </div>
      </section>

      <footer className="home-footer">
        <div className="home-footer-links">
          <Button className="home-footer-btn" onClick={openTwitter}>
            Contact
          </Button>
          <Button className="home-footer-btn" onClick={() => navigate('/privacy')}>
            Privacy
          </Button>
        </div>
        <Typography className="home-footer-address">
          1150 S Olive St, Los Angeles, CA 90015
        </Typography>
        <Typography className="home-footer-copy">
          © {new Date().getFullYear()} Hashely. All rights reserved.
        </Typography>
      </footer>
    </div>
  );
};

export default Home;
